import {useStore} from 'vuex';
import {avatarUrl as initialsAvatarUrl} from '@/utils/avatar';
import {computed} from 'vue';
import type {StoreLoaders} from '@/types/common';
import type {RenterInterface} from '@/types/user';
import type {fetchRenterOptions} from '@/store/modules/renters';

export interface MergeCustomers {
    main: string;
    second: string;
    mode: 'simple' | 'advanced';
}

export function useCustomer() {
    const store = useStore();


    const customerCount = computed(() => store.getters['renters/totalCustomers']);
    const customersLoading = computed<StoreLoaders>(() => store.getters['renters/rentersLoading']);
    const customersInit = computed(() => store.getters['renters/init']);
    const allCustomers = computed(() => store.getters['renters/allRenters']);
    const customers = computed<RenterInterface[]>(() => store.getters['renters/renters']);
    const dialogs = computed(() => store.getters['renters/dialogs']);
    const customerTagListCounts = computed<Record<string, number>>(() => store.getters['renters/customerTagListCounts']);
    const customersQuery = computed(() => store.getters['renters/customersQuery']);
    const customerPagination = computed(() => store.getters['renters/customerPagination']);
    const customerCreatedAt = computed<number | null>(() => store.getters['renters/customerCreatedAt']);

    const stripeCustomerObj = computed(() => store.getters['renters/stripeCustomerObj']);

    const customerForm = computed(() => {
        return store.getters['renters/renterForm'];
    });


    const createCustomer = async () => {
        return await store.dispatch('renters/createRenter');
    }

    const customerPayments = async (r_id: string) => {
        await store.dispatch('renters/renterPayments', r_id);
    }

    const mergeCustomers = async (data: MergeCustomers) => {
        return await store.dispatch('renters/mergeCustomers', data);
    }

    const updateCustomer = async (customer: RenterInterface) => {
        return await store.dispatch('renters/updateRenter', customer);
    }

    const resetCustomerStates = () => store.commit('renters/RESET_RENTER_STATES');

    const deleteCustomer = async (customer: RenterInterface) => {
        return await store.dispatch('renters/deleteRenter', customer);
    }

    const searchedCustomers = computed<RenterInterface[]>(() => store.getters['renters/searchedRenters']);

    const currentCustomer = computed<RenterInterface>({
        get() {
            return store.getters['renters/currentRenter'];
        },
        set(renter: RenterInterface) {
            store.commit('renters/SET_RENTER', renter);
        }
    })

    const avatarUrl = (customer: RenterInterface) => initialsAvatarUrl(customer);

    const searchCustomer = async (query: string) => {
        await store.dispatch('renters/searchRenters', query);
    }

    const renterCharges = async (r_id: string) => {
        await store.dispatch('renters/renterCharges', r_id);
    }

    const stripeCustomer = async (r_id: string) => {
        return store.dispatch('renters/stripeCustomer', r_id);
    }

    const fetchRentersForExport = async () => {
        await store.dispatch('renters/fetchRentersForExport');
    }

    const fetchCustomer = async (data: fetchRenterOptions) => {
        return await store.dispatch('renters/fetchRenter', data);
    }

    const setCustomerDialog = (topic: string, value: boolean) => {
        store.commit('renters/SET_RENTERS_DIALOG', {topic, value});
    }

    const updateCustomerQuery = (query: { key: string, value: any }) => {
        store.commit('renters/SET_CUSTOMER_QUERY', query);
    }

    const fetchCompanyCustomers = async () => {
        await store.dispatch('renters/fetchCompanyRenters');
    }

    const resetCustomer = async () => {
        return store.dispatch('renters/resetRenter');
    };

    const blockCustomer = async (data: {
        r_id: string,
        reason: string,
        fleetwire?: boolean,
        square?: boolean,
        stripe?: boolean
    }) => {
        return store.dispatch('renters/blockRenter', data);
    }

    const unblockRenter = async (data: { r_id: string }) => {
        return store.dispatch('renters/unblockRenter', data);
    }

    return {
        dialogs,
        customers,
        allCustomers,
        customerCount,
        customersLoading,
        customersInit,
        currentCustomer,
        customerTagListCounts,
        customersQuery,
        customerPagination,
        customerCreatedAt,
        searchedCustomers,
        stripeCustomerObj,
        customerForm,
        avatarUrl,
        customerPayments,
        renterCharges,
        stripeCustomer,
        deleteCustomer,
        createCustomer,
        updateCustomer,
        searchCustomer,
        updateCustomerQuery,
        fetchCustomer,
        setCustomerDialog,
        fetchRentersForExport,
        fetchCompanyCustomers,
        resetCustomer,
        blockCustomer,
        unblockRenter,
        resetCustomerStates,
        mergeCustomers,
    }
}